import { revalidateLogic } from '@tanstack/react-form'

import { useAppForm } from '@ui/components/forms'
import { FieldGroup } from '@ui/components/shadcn/ui/field'

import { useCheckIdentifierLazyQuery } from '@graphql/react-apollo'

import {
  useAuthAnalytics,
  useAuthScreenView,
} from '@web-app/components/auth/analytics'
import { AuthLegalNotice } from '@web-app/components/auth/AuthLegalNotice'
import { OAuthLinks } from '@web-app/components/auth/OAuth'
import { GENERIC_ERROR } from '@web-app/lib/errors/transportFailure'
import {
  classifyIdentifier,
  ClassifiedIdentifier,
  identifierSchema,
} from '@web-app/schemas/auth'

import { useSendSMSLoginCode } from './useSendSMSLoginCode'

interface Props {
  onSignUp: (identifier: ClassifiedIdentifier) => void
  onSignIn: (args: {
    identifier: ClassifiedIdentifier
    smsSentAt?: number
  }) => void
  initialValue?: string
}

export function IdentifierForm({ onSignUp, onSignIn, initialValue = '' }: Props) {
  const [checkIdentifier] = useCheckIdentifierLazyQuery({
    fetchPolicy: 'network-only',
  })
  const sendSmsLoginCode = useSendSMSLoginCode()
  const { trackTap } = useAuthAnalytics()
  useAuthScreenView('entryRoute')

  const form = useAppForm({
    defaultValues: { identifier: initialValue },
    validationLogic: revalidateLogic(),
    validators: {
      onDynamic: identifierSchema,
      onSubmitAsync: async ({ value }) => {
        const identifier = classifyIdentifier({ raw: value.identifier })
        if (!identifier) return { form: GENERIC_ERROR }

        trackTap('entryRouteNext')
        const { data, error } = await checkIdentifier({
          variables:
            identifier.kind === 'email'
              ? { email: identifier.value }
              : { phone: { code: 1, number: identifier.value } },
        })
        // A failed lookup is a transport failure: the toast link owns it.
        if (error) throw error

        const result = data?.checkIdentifier
        switch (result?.action) {
          case 'sign_up':
            onSignUp(identifier)
            return null
          case 'sign_in':
          case 'link_required': {
            // A phone signs in by SMS code; send it now, in the same tap. If
            // it fails, smsSentAt stays unset and the next view offers "Send code".
            let smsSentAt: number | undefined
            if (
              identifier.kind === 'phone' &&
              (await sendSmsLoginCode(identifier.value, { bestEffort: true }))
            ) {
              smsSentAt = Date.now()
            }
            onSignIn({ identifier, smsSentAt })
            return null
          }
          case 'error':
            return {
              fields: { identifier: { message: result.message ?? GENERIC_ERROR } },
            }
          default:
            return { form: GENERIC_ERROR }
        }
      },
    },
  })

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <FieldGroup className="gap-2">
        <form.AppField name="identifier">
          {(field) => (
            <field.TextField
              label="Email or mobile number"
              hideLabel
              placeholder="Email or mobile number"
              autoComplete="username"
              inputMode="email"
            />
          )}
        </form.AppField>
      </FieldGroup>
      <form.AppForm>
        <div className="mt-2 flex flex-col gap-2">
          <form.SubmitButton>Continue</form.SubmitButton>
          <form.FormError />
        </div>
      </form.AppForm>
      <OAuthLinks
        positionOr="top"
        textSign="continue"
        onProviderClick={(provider) =>
          trackTap(provider === 'google' ? 'entryRouteGoogle' : 'entryRouteApple')
        }
      />
      <AuthLegalNotice />
    </form>
  )
}
